import { useCallback, useEffect, useRef, useState } from "react";
import useFetch from "use-http";

export interface BmsAppointment {
	// BMS's own identifier for the appointment (`appointment_uid`, new in the
	// v7 endpoints doc). The only stable key for a single appointment — the
	// times change when the endcustomer moves it.
	appointmentUid: string | null;
	consultantEmail: string;
	// ISO-8601 with an offset since v7, e.g. "2027-07-29T13:30:00+00:00".
	// Null when BMS sent something unparseable.
	appointmentStart: string | null;
	appointmentEnd: string | null;
	// `appointment_link`, added by BMS in September 2026: the detail page of
	// this one appointment, where the endcustomer can move or cancel it.
	// Null for appointments BMS sent without one.
	appointmentLink: string | null;
}

interface UseBmsAppointmentsResult {
	appointments: BmsAppointment[];
	bookingLink: string | null;
	loading: boolean;
	loaded: boolean;
	// True only once a check has actually succeeded. BMS's appointments
	// endpoint (3.2) is new and may 404 until they deploy it — that must
	// never look the same as "confirmed zero appointments" to callers,
	// since the two call for different UI (stay silent vs. show a prompt).
	available: boolean;
	refresh: () => Promise<void>;
}

// Proxies GET /bathconfigurator/endcustomer/appointments (BMS endpoint 3.2).
// Fetches once when `enabled` becomes true — never on a timer. Callers get a
// `refresh()` to re-check on demand (e.g. a manual "refresh" button), since
// there is deliberately no polling: the backend cache is kept fresh via BMS's
// booking-created/booking-cancelled webhooks instead.
const useBmsAppointments = (enabled: boolean): UseBmsAppointmentsResult => {
	const { get, response, loading } = useFetch(
		"/bathconfigurator/endcustomer/appointments",
	);
	const [appointments, setAppointments] = useState<BmsAppointment[]>([]);
	const [bookingLink, setBookingLink] = useState<string | null>(null);
	const [available, setAvailable] = useState(false);
	const [loaded, setLoaded] = useState(false);
	const fetchedRef = useRef(false);

	const refresh = useCallback(async () => {
		const result = await get();
		if (response.ok) {
			setAppointments(result?.appointments ?? []);
			setBookingLink(result?.bookingLink ?? null);
			setAvailable(true);
		} else {
			setAvailable(false);
		}
		setLoaded(true);
	}, [get, response]);

	useEffect(() => {
		// Re-armed on the way out, so reopening fetches again instead of showing
		// what was true the first time. Matters now that the caller keeping this
		// hook alive (BmsAppointmentButton) outlives the dialog it feeds.
		if (!enabled) {
			fetchedRef.current = false;
			return;
		}
		if (fetchedRef.current) return;
		fetchedRef.current = true;
		refresh();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [enabled]);

	return { appointments, bookingLink, loading, loaded, available, refresh };
};

export default useBmsAppointments;
